if (!customElements.get('mav-havik15')) {
  customElements.define('mav-havik15', class extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;
      this.abort = new AbortController();
      const options = { signal: this.abort.signal };
      this.reducedMotion = matchMedia('(prefers-reduced-motion: reduce)');
      this.previewsPaused = this.reducedMotion.matches;
      this.previewStates = new Map();
      this.querySelectorAll('[data-play-video]').forEach((button) => {
        this.previewStates.set(button.parentElement, { button, visible: false, player: null, loading: false, version: 0, failed: false });
      });
      const previewToggle = this.querySelector('[data-preview-toggle]');
      const updateToggle = () => {
        previewToggle.textContent = this.previewsPaused ? previewToggle.dataset.playLabel : previewToggle.dataset.pauseLabel;
        previewToggle.setAttribute('aria-pressed', String(this.previewsPaused));
      };
      updateToggle();
      this.reducedMotion.addEventListener('change', (event) => {
        this.previewsPaused = event.matches;
        updateToggle();
        this.refreshPreviews();
      }, options);
      document.addEventListener('visibilitychange', () => this.refreshPreviews(), options);
      this.videoObserver = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          this.previewStates.get(entry.target).visible = entry.isIntersecting && entry.intersectionRatio >= 0.3;
        });
        this.refreshPreviews();
      }, { threshold: [0, 0.3] });
      this.previewStates.forEach((state, container) => this.videoObserver.observe(container));
      this.addEventListener('click', (event) => {
        const thumb = event.target.closest('[data-gallery-image]');
        if (thumb) {
          const image = this.querySelector('[data-gallery-stage] img');
          image.removeAttribute('srcset');
          image.removeAttribute('sizes');
          image.src = thumb.dataset.galleryImage;
          image.alt = thumb.dataset.imageAlt;
          image.style.objectFit = thumb.dataset.imageFit;
          this.querySelectorAll('[data-gallery-image]').forEach((button) => button.setAttribute('aria-pressed', String(button === thumb)));
        }
        const quantityButton = event.target.closest('[data-quantity-step]');
        if (quantityButton) {
          const input = this.querySelector('[name="quantity"]');
          if (Number(quantityButton.dataset.quantityStep) > 0) input.stepUp(); else input.stepDown();
          input.dispatchEvent(new Event('change', { bubbles: true }));
        }
        const play = event.target.closest('[data-play-video]');
        if (play) {
          this.closeVideo();
          this.fullVideo = play;
          this.refreshPreviews();
          const frame = document.createElement('iframe');
          const id = play.dataset.playVideo;
          if (!/^[A-Za-z0-9_-]{11}$/.test(id)) return;
          const start = Math.max(0, parseInt(play.dataset.videoStart, 10) || 0);
          frame.src = `https://www.youtube.com/embed/${id}?start=${start}&autoplay=1&rel=0&playsinline=1`;
          frame.title = play.dataset.videoTitle;
          frame.allow = 'accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share';
          frame.allowFullscreen = true;
          frame.referrerPolicy = 'strict-origin-when-cross-origin';
          frame.className = 'mav15__full-video';
          play.hidden = true;
          play.parentElement.appendChild(frame);
          play.closest('article').querySelector('[data-close-video]').hidden = false;
          frame.focus();
        }
        if (event.target.closest('[data-close-video]')) {
          const button = this.fullVideo;
          this.closeVideo();
          this.refreshPreviews();
          button?.focus();
        }
        if (event.target.closest('[data-preview-toggle]')) {
          this.previewsPaused = !this.previewsPaused;
          updateToggle();
          this.refreshPreviews();
        }
        const scroll = event.target.closest('[data-video-scroll]');
        if (scroll) {
          const track = this.querySelector('[data-video-track]');
          const distance = track.querySelector('article').getBoundingClientRect().width + 14;
          track.scrollBy({ left: Number(scroll.dataset.videoScroll) * distance, behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth' });
        }
      }, options);
      const track = this.querySelector('[data-video-track]');
      const refreshArrows = () => {
        this.querySelector('[data-video-scroll="-1"]').disabled = track.scrollLeft < 5;
        this.querySelector('[data-video-scroll="1"]').disabled = track.scrollLeft + track.clientWidth >= track.scrollWidth - 5;
      };
      track.addEventListener('scroll', refreshArrows, options);
      window.addEventListener('resize', refreshArrows, options);
      refreshArrows();
      const variantSelect = this.querySelector('[data-variant-select]');
      variantSelect?.addEventListener('change', () => {
        const option = variantSelect.selectedOptions[0];
        this.querySelectorAll('[data-product-price]').forEach(node => { node.textContent = option.dataset.price; });
        const compare = this.querySelector('[data-compare-price]');
        if (compare) { compare.textContent = option.dataset.comparePrice; compare.hidden = !option.dataset.comparePrice; }
        this.querySelector('[type="submit"]').disabled = option.dataset.available !== 'true';
        const url = new URL(window.location.href);
        url.searchParams.set('variant', option.value);
        history.replaceState({}, '', url);
      }, options);
      const sticky = this.querySelector('[data-sticky-buy]');
      const hero = this.querySelector('.mav15__hero');
      const buy = this.querySelector('.mav15__purchase');
      const bottom = this.querySelector('.mav15__bottom-cta');
      const refreshSticky = () => {
        const h = hero.getBoundingClientRect();
        const b = buy.getBoundingClientRect();
        const f = bottom.getBoundingClientRect();
        sticky.hidden = !(h.bottom < 0 && b.bottom < 0 && f.top > innerHeight);
      };
      this.observer = new IntersectionObserver(refreshSticky);
      [hero, buy, bottom].forEach((node) => this.observer.observe(node));
      refreshSticky();
    }
    closeVideo() {
      this.querySelectorAll('.mav15__full-video').forEach((frame) => frame.remove());
      this.querySelectorAll('[data-play-video]').forEach((button) => { button.hidden = false; });
      this.querySelectorAll('[data-close-video]').forEach((button) => { button.hidden = true; });
      this.fullVideo = null;
    }
    static loadVideoApi() {
      if (window.YT?.Player) return Promise.resolve(window.YT);
      if (!this.videoApiPromise) {
        this.videoApiPromise = new Promise((resolve, reject) => {
          const timeout = setTimeout(() => reject(new Error('Video preview API unavailable')), 15000);
          const previous = window.onYouTubeIframeAPIReady;
          window.onYouTubeIframeAPIReady = () => {
            clearTimeout(timeout);
            resolve(window.YT);
            if (typeof previous === 'function') previous();
          };
          if (!document.querySelector('script[src="https://www.youtube.com/iframe_api"]')) {
            const script = document.createElement('script');
            script.src = 'https://www.youtube.com/iframe_api';
            script.async = true;
            script.addEventListener('error', () => { clearTimeout(timeout); reject(new Error('Video preview API unavailable')); }, { once: true });
            document.head.appendChild(script);
          }
        });
      }
      return this.videoApiPromise;
    }
    stopPreview(container, state) {
      state.version += 1;
      state.loading = false;
      if (state.player) state.player.destroy();
      state.player = null;
      container.querySelectorAll('.mav15__preview-mount').forEach((mount) => mount.remove());
      container.classList.remove('is-previewing');
    }
    refreshPreviews() {
      this.previewStates.forEach((state, container) => {
        if (this.previewsPaused || this.fullVideo || document.hidden || !state.visible) {
          this.stopPreview(container, state);
        } else if (!state.player && !state.loading && !state.failed) {
          this.startPreview(container, state);
        }
      });
    }
    async startPreview(container, state) {
      const id = state.button.dataset.playVideo;
      if (!/^[A-Za-z0-9_-]{11}$/.test(id)) return;
      const version = ++state.version;
      state.loading = true;
      try {
        const api = await this.constructor.loadVideoApi();
        if (version !== state.version || !this.isConnected) return;
        const mount = document.createElement('div');
        mount.className = 'mav15__preview-mount';
        container.appendChild(mount);
        const start = Math.max(0, parseInt(state.button.dataset.videoStart, 10) || 0);
        state.player = new api.Player(mount, {
          host: 'https://www.youtube.com',
          width: '100%', height: '100%', videoId: id,
          playerVars: { autoplay: 1, mute: 1, controls: 0, disablekb: 1, fs: 0, playsinline: 1, loop: 1, playlist: id, start, rel: 0, origin: location.origin },
          events: {
            onReady: (event) => {
              if (state.version !== version) return;
              const frame = event.target.getIframe();
              frame.title = state.button.dataset.videoTitle;
              frame.tabIndex = -1;
              frame.setAttribute('aria-hidden', 'true');
              event.target.mute();
              event.target.playVideo();
            },
            onStateChange: (event) => {
              if (state.version !== version) return;
              if (event.data === api.PlayerState.PLAYING) container.classList.add('is-previewing');
            },
            onError: () => {
              state.failed = true;
              this.stopPreview(container, state);
            }
          }
        });
        state.loading = false;
      } catch {
        if (state.version !== version) return;
        state.failed = true;
        this.stopPreview(container, state);
      }
    }
    disconnectedCallback() {
      this.abort?.abort();
      this.observer?.disconnect();
      this.videoObserver?.disconnect();
      this.previewStates?.forEach((state, container) => this.stopPreview(container, state));
      this.closeVideo();
      this.initialized = false;
    }
  });
}
